import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Users,
  Image,
  Book,
  MapPin,
  FileText,
  Loader2,
  LogOut,
  BookOpen,
  Contact,
  ClipboardList,
  ChevronLeft,
  ShieldAlert,
  BarChart3,
  Sun,
  Moon,
  Settings,
  UserCircle,
  MessageSquare,
  Trash2,
  Zap,
  Building2,
} from 'lucide-react';
import { useSessionStore } from '../../stores/useSessionStore.ts';
import { useProjectStore } from '../../stores/useProjectStore.ts';
import { useDataStore } from '../../stores/useDataStore.ts';
import { useUIStore } from '../../stores/useUIStore.ts';
import { useOrganizationStore } from '../../stores/useOrganizationStore.ts';
import { PecktreeLogo } from '../shared/PecktreeLogo.tsx';
import NotificationBell from '../shared/NotificationBell.tsx';
import { useTranslation } from '../../i18n.ts';
import { useTierAccess } from '../../hooks/useTierAccess.ts';
import { useTheme } from '../../hooks/useTheme.ts';
import { ApiService } from '../../services/ApiService.ts';
import type { PresenceUser } from '../../types.ts';

interface AppSidebarProps {
  wsConnected: boolean;
  wsPresenceWithMe: (PresenceUser & { isMe: boolean })[];
  queueSize: number;
  qualityIssueCount: number;
  onLogout: () => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  wsConnected,
  wsPresenceWithMe,
  queueSize,
  qualityIssueCount,
  onLogout,
}) => {
  const { t } = useTranslation();
  const { toggleTheme, isDark } = useTheme();
  const { isTrialActive, trialDaysLeft, tierNameKey, storedTier } = useTierAccess();
  const location = useLocation();

  // ── Zustand stores ──
  const { user, appVersion, pendingInvitations, organizationId } = useSessionStore();
  const { currentProject } = useProjectStore();
  const { people } = useDataStore();
  const {
    sidebarOpen,
    setSidebarOpen,
    sidebarCollapsed,
    toggleSidebarCollapse,
    setShowProjectSwitcher,
  } = useUIStore();
  // Plan 3 #778 — lees currentOrg via selector zodat sidebar alleen rerendert
  // bij wijziging van currentOrg, niet bij elke org-store-mutatie.
  const currentOrg = useOrganizationStore((s) => s.currentOrg);
  const loadOrg = useOrganizationStore((s) => s.loadOrg);

  // Lazy-load org bij eerste render als sessionOrgId bestaat maar currentOrg
  // nog niet (of voor een andere org gezet is). Sidebar is de centrale plek
  // omdat hij altijd gemount is zodra een ingelogde gebruiker iets ziet.
  useEffect(() => {
    if (organizationId && currentOrg?.id !== organizationId) {
      loadOrg(organizationId);
    }
  }, [organizationId, currentOrg?.id, loadOrg]);

  return (
    <aside
      className={`sidebar no-print ${sidebarCollapsed ? 'collapsed' : ''} ${sidebarOpen ? 'mobile-open' : ''}`}
    >
      {/* Header: Logo + Wordmark + Toggle */}
      <div className="sidebar-header">
        <Link to="/" className="sidebar-logo" title="Pecktree">
          <PecktreeLogo size={36} />
        </Link>
        <div className="sidebar-wordmark">
          <span className="wordmark-text">
            <span className="wordmark-peck">Peck</span>
            <span className="wordmark-tree">tree</span>
          </span>
        </div>
        <button
          onClick={toggleSidebarCollapse}
          className="sidebar-toggle"
          title={sidebarCollapsed ? t('uiMenuExpand') : t('uiMenuCollapse')}
          aria-label={sidebarCollapsed ? t('uiMenuExpand') : t('uiMenuCollapse')}
        >
          <ChevronLeft size={16} />
        </button>
      </div>

      {/* Project selector */}
      <div
        className="project-selector"
        onClick={() => setShowProjectSwitcher(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowProjectSwitcher(true);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={t('projectSelectorAria')}
        data-tour="project-switcher"
      >
        <div className="project-selector-inner">
          <div className="project-avatar">
            {(currentProject?.name || 'P').charAt(0).toUpperCase()}
          </div>
          <div className="project-info">
            <div className="project-name">{currentProject?.name || t('selectProject')}</div>
            <div className="project-meta">
              <span className="project-count">
                {/* Enkelvoud bij 1 — anders staat er "1 personen". Beide keys
                    bestaan al in nl/en, dus geen nieuwe vertaling nodig. */}
                {people.length} {t(people.length === 1 ? 'person' : 'people').toLowerCase()}
              </span>
            </div>
            {/* NC #640-vervolg: eigen regel i.p.v. inline naast de personen-telling —
                de ring rond de bolletjes viel anders onder de kaart weg omdat die
                geen ruimte reserveerde voor de shadow-overhang van ring-2. */}
            {wsConnected && wsPresenceWithMe.length > 1 && (
              <div className="project-online-row">
                <span className="project-online-label">{t('online')}:</span>
                <span className="online-indicator flex items-center gap-0.5">
                  {wsPresenceWithMe
                    .filter((p) => !p.isMe)
                    .slice(0, 3)
                    .map((p, i) => (
                      <span
                        key={p.id || i}
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-pt-gold text-pt-forest text-[8px] font-black ring-2 ring-white -ml-1 first:ml-0"
                        title={p.displayName || p.email}
                      >
                        {p.avatarUrl ? (
                          <img
                            src={p.avatarUrl}
                            alt={p.displayName || p.email || t('uiTeamMember')}
                            className="w-full h-full rounded-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          (p.displayName || p.email)?.[0]?.toUpperCase()
                        )}
                      </span>
                    ))}
                  {wsPresenceWithMe.filter((p) => !p.isMe).length > 3 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-pt-sage text-pt-forest text-[7px] font-black ring-2 ring-white -ml-1">
                      +{wsPresenceWithMe.filter((p) => !p.isMe).length - 3}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
          <span className="project-chevron">
            <svg viewBox="0 0 24 24">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </div>
        {pendingInvitations.length > 0 && !sidebarCollapsed && (
          <span className="nav-badge badge-gold" style={{ position: 'absolute', top: 4, right: 4 }}>
            {pendingInvitations.length}
          </span>
        )}
      </div>

      {/* Offline queue warning */}
      {!sidebarCollapsed && queueSize > 0 && (
        <div
          style={{
            margin: '0 10px 6px',
            padding: '8px 10px',
            background: 'rgba(234,179,8,0.1)',
            border: '1px solid rgba(234,179,8,0.2)',
            borderRadius: 'var(--radius-lg)',
            fontSize: 11,
            color: '#eab308',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
          role="status"
          aria-live="polite"
        >
          <Loader2 className="animate-spin" size={14} />
          <span style={{ fontWeight: 700 }}>
            {queueSize} {t('operationsInQueue')}
          </span>
        </div>
      )}

      {/* Navigation */}
      <nav className="nav-scroll" onClick={() => setSidebarOpen(false)}>
        {/* Main section */}
        <div className="nav-section">
          <h2 className="nav-section-label">{t('navigation')}</h2>
          <Link
            to="/"
            title={t('dashboard')}
            className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}
            aria-current={location.pathname === '/' ? 'page' : undefined}
          >
            <span className="nav-icon">
              <Layout size={16} />
            </span>
            <span className="nav-label">{t('dashboard')}</span>
          </Link>
          <Link
            to="/tree"
            title={t('tree')}
            className={`nav-item ${location.pathname === '/tree' ? 'active' : ''}`}
            aria-current={location.pathname === '/tree' ? 'page' : undefined}
            data-tour="tree-link"
          >
            <span className="nav-icon">
              <Zap size={16} />
            </span>
            <span className="nav-label">{t('tree')}</span>
          </Link>
          <Link
            to="/people"
            title={t('people')}
            className={`nav-item ${location.pathname === '/people' ? 'active' : ''}`}
            aria-current={location.pathname === '/people' ? 'page' : undefined}
            data-tour="people-link"
          >
            <span className="nav-icon">
              <Contact size={16} />
            </span>
            <span className="nav-label">{t('people')}</span>
          </Link>
          <Link
            to="/sources"
            title={t('sources')}
            className={`nav-item ${location.pathname === '/sources' ? 'active' : ''}`}
            aria-current={location.pathname === '/sources' ? 'page' : undefined}
            data-tour="sources-link"
          >
            <span className="nav-icon">
              <Book size={16} />
            </span>
            <span className="nav-label">{t('sources')}</span>
          </Link>
          <Link
            to="/places"
            title={t('places')}
            className={`nav-item ${location.pathname === '/places' ? 'active' : ''}`}
            aria-current={location.pathname === '/places' ? 'page' : undefined}
          >
            <span className="nav-icon">
              <MapPin size={16} />
            </span>
            <span className="nav-label">{t('places')}</span>
          </Link>
          <Link
            to="/media"
            title={t('media')}
            className={`nav-item ${location.pathname === '/media' ? 'active' : ''}`}
            aria-current={location.pathname === '/media' ? 'page' : undefined}
          >
            <span className="nav-icon">
              <Image size={16} />
            </span>
            <span className="nav-label">{t('media')}</span>
          </Link>
          <Link
            to="/stories"
            title={t('stories')}
            className={`nav-item ${location.pathname === '/stories' ? 'active' : ''}`}
            aria-current={location.pathname === '/stories' ? 'page' : undefined}
          >
            <span className="nav-icon">
              <BookOpen size={16} />
            </span>
            <span className="nav-label">{t('lifeStories')}</span>
          </Link>
        </div>

        <div className="nav-divider" />

        {/* Research section */}
        <div className="nav-section">
          <h2 className="nav-section-label">{t('research')}</h2>
          <Link
            to="/planner"
            title={t('planner')}
            className={`nav-item ${location.pathname === '/planner' ? 'active' : ''}`}
            aria-current={location.pathname === '/planner' ? 'page' : undefined}
          >
            <span className="nav-icon">
              <ClipboardList size={16} />
            </span>
            <span className="nav-label">{t('researchPlanner')}</span>
          </Link>
          <Link
            to="/quality"
            title={t('dataQuality')}
            className={`nav-item ${location.pathname === '/quality' ? 'active' : ''}`}
            aria-current={location.pathname === '/quality' ? 'page' : undefined}
          >
            <span className="nav-icon">
              <ShieldAlert size={16} />
            </span>
            <span className="nav-label">{t('dataQuality')}</span>
            {qualityIssueCount > 0 && (
              <span className="nav-badge">
                {qualityIssueCount > 99 ? '99+' : qualityIssueCount}
              </span>
            )}
          </Link>
          <Link
            to="/analyse"
            title={t('statistics')}
            className={`nav-item ${location.pathname === '/analyse' ? 'active' : ''}`}
            aria-current={location.pathname === '/analyse' ? 'page' : undefined}
          >
            <span className="nav-icon">
              <BarChart3 size={16} />
            </span>
            <span className="nav-label">{t('statistics')}</span>
          </Link>
        </div>

        <div className="nav-divider" />

        {/* Publication section */}
        <div className="nav-section">
          <h2 className="nav-section-label">{t('publication')}</h2>
          <Link
            to="/reports"
            title={t('reports')}
            className={`nav-item ${location.pathname === '/reports' ? 'active' : ''}`}
            aria-current={location.pathname === '/reports' ? 'page' : undefined}
          >
            <span className="nav-icon">
              <FileText size={16} />
            </span>
            <span className="nav-label">{t('reports')}</span>
          </Link>
        </div>

        {/* Organisatie-sectie — Plan 3 #778: alleen tonen als currentOrg geladen is. */}
        {currentOrg && (
          <>
            <div className="nav-divider" />
            <div className="nav-section">
              <h2 className="nav-section-label">{t('organization')}</h2>
              <Link
                to="/organisatie"
                title={currentOrg.name}
                className={`nav-item ${
                  location.pathname.startsWith('/organisatie') ? 'active' : ''
                }`}
                aria-current={location.pathname.startsWith('/organisatie') ? 'page' : undefined}
              >
                <span className="nav-icon">
                  <Building2 size={16} />
                </span>
                <span className="nav-label">{currentOrg.name}</span>
              </Link>
            </div>
          </>
        )}

        <div className="nav-divider" />

        {/* Support */}
        <Link
          to="/support"
          title={t('support')}
          className={`nav-item ${location.pathname === '/support' ? 'active' : ''}`}
          aria-current={location.pathname === '/support' ? 'page' : undefined}
        >
          <span className="nav-icon">
            <MessageSquare size={16} />
          </span>
          <span className="nav-label">{t('support')}</span>
        </Link>

        {/* Instellingen */}
        <Link
          to="/account"
          title={t('settings')}
          className={`nav-item ${location.pathname === '/account' ? 'active' : ''}`}
          aria-current={location.pathname === '/account' ? 'page' : undefined}
        >
          <span className="nav-icon">
            <Settings size={16} />
          </span>
          <span className="nav-label">{t('settings')}</span>
        </Link>

        <div className="nav-divider" />

        {/* Prullenbak */}
        <Link
          to="/deleted"
          title={t('deletedItems')}
          className={`nav-item ${location.pathname === '/deleted' ? 'active' : ''}`}
          aria-current={location.pathname === '/deleted' ? 'page' : undefined}
        >
          <span className="nav-icon">
            <Trash2 size={16} />
          </span>
          <span className="nav-label">{t('deletedItems')}</span>
        </Link>
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <div className="footer-actions">
          <div className="user-avatar">
            {(user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="user-info">
            <div className="user-name">{user?.displayName || user?.email || ''}</div>
            {isTrialActive ? (
              <Link
                to="/account?tab=billing"
                className={`user-tier trial-badge${trialDaysLeft <= 3 ? ' urgent' : ''}`}
                title={t('trialBadgeTooltip')}
              >
                {/* #1730 — de proef heet naar zijn plan (Professional; oudere proeven Onderzoeker). */}
                {t('trialBadge', { plan: t(`tierName_${storedTier}`) })} — {trialDaysLeft}{' '}
                {trialDaysLeft === 1 ? t('day') : t('days')}
              </Link>
            ) : (
              <Link to="/account?tab=billing" className="user-tier">
                {t(tierNameKey)}
              </Link>
            )}
          </div>
          <div className="footer-icons">
            <NotificationBell />
            <button
              className="footer-icon-btn"
              onClick={toggleTheme}
              title={isDark ? t('uiLightMode') : t('uiDarkMode')}
              aria-label={isDark ? t('uiSwitchToLightMode') : t('uiSwitchToDarkMode')}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <Link to="/account" className="footer-icon-btn" title={t('account')}>
              <UserCircle size={16} />
            </Link>
          </div>
        </div>
        <button onClick={onLogout} className="logout-btn" title={t('logout')}>
          <LogOut size={15} />
          <span className="logout-label">{t('logout')}</span>
        </button>
        {!sidebarCollapsed && (
          <div
            style={{
              padding: '4px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <span
              style={{ fontSize: 10, color: 'rgba(188,198,184,0.25)', fontFamily: 'monospace' }}
            >
              {appVersion ? `v${appVersion}` : ''}
            </span>
          </div>
        )}
      </div>
    </aside>
  );
};

export default AppSidebar;
